import type { SmileLinkEntryInput } from "@/lib/utils/smile-chrome/link-entries";

/**
 * The /smile header's nav row, read from the Contentful entry Bailey Hill
 * edits (bbSmileHeaderNav, a Navigation Group) so that his edits reach EVERY
 * render path of the header, the way the footer's entry already reaches every
 * footer (footer-entry.ts, 2026-09-09):
 *
 *   - a canvas placement whose Nav Items variable is bound to the group's
 *     Links field: the Experiences SDK hands the atom the entries itself;
 *   - a canvas placement left unbound, the Smile Header atom inside the shared
 *     chrome slot, and the code fallback app/[...slug]/page.tsx and
 *     app/smile/schedule/page.tsx render when no authored header resolves:
 *     until this module those three rendered the code defaults whatever
 *     Bailey published, so an edit to the entry changed only the pages whose
 *     atom happened to be bound.
 *
 * Input is the CDA REST shape with links resolved (contentful.js
 * withoutUnresolvableLinks, include 2): the group's `links` arrive as the
 * Navigation Link entries themselves, each with its own references one level
 * down. The rows are handed over AS ENTRIES, not mapped here: the atom runs
 * toSmileLinkItems on them exactly as it does on a Studio binding, so the
 * href hygiene (origins stripped, allowlisted schemes) and the canvas warning
 * for a skipped link are one code path whichever way the entries arrived.
 */

type Rec = Record<string, unknown>;

const isRecord = (value: unknown): value is Rec =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const fieldsOf = (entry: unknown): Rec =>
  isRecord(entry) && isRecord(entry.fields) ? entry.fields : {};

const contentTypeOf = (entry: unknown): string | undefined => {
  if (!isRecord(entry) || !isRecord(entry.sys)) return undefined;
  const contentType = entry.sys.contentType;
  return isRecord(contentType) &&
    isRecord(contentType.sys) &&
    typeof contentType.sys.id === "string"
    ? contentType.sys.id
    : undefined;
};

/** An entry (or a Link stub the SDK could not resolve): anything with a sys object. */
const isEntryLike = (value: unknown): value is SmileLinkEntryInput =>
  isRecord(value) && isRecord(value.sys);

/** TAG's header sub-nav container; its Links field is what the atom's Nav Items binds. */
const SMILE_HEADER_NAV_GROUP_CONTENT_TYPE = "navigationGroup";

/**
 * The Links of a Navigation Group entry, or undefined when there is nothing
 * to render: the entry is missing, is not a Navigation Group, or its Links
 * field is empty. Rows that are not entries (a malformed record) are dropped
 * here; rows the atom cannot render are its own decision (toSmileLinkItems).
 */
export function smileHeaderNavFromGroup(group: unknown): SmileLinkEntryInput[] | undefined {
  if (contentTypeOf(group) !== SMILE_HEADER_NAV_GROUP_CONTENT_TYPE) return undefined;
  const links = fieldsOf(group).links;
  if (!Array.isArray(links)) return undefined;
  const entries = links.filter(isEntryLike);
  return entries.length > 0 ? entries : undefined;
}

/**
 * How a placement combines what the author bound on the node with what the
 * header entry says. A bound Nav Items (the SDK hands the entries over, never
 * an empty array) wins: the author chose that list. Unbound - undefined, or
 * the empty array a plain caller may pass - follows the entry. No entry
 * (nothing published on this door) leaves the authored value alone, so the
 * atom's defaults render exactly as before the entry existed.
 */
export function mergeSmileHeaderNavItems(
  authored: ReadonlyArray<SmileLinkEntryInput> | undefined,
  entry: ReadonlyArray<SmileLinkEntryInput> | null | undefined,
): ReadonlyArray<SmileLinkEntryInput> | undefined {
  if (Array.isArray(authored) && authored.length > 0) return authored;
  if (entry && entry.length > 0) return entry;
  return authored;
}
